import { z } from 'zod';
import { milliUnitsSchema, positiveMilliUnitsSchema, stockUnitSchema } from '../inventory/units.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { isoDateStringSchema, objectIdSchema, paginationQuerySchema } from './common.js';

/**
 * What an order NEEDS before it can be made.
 *
 * A shop books a bridal lehenga and then has to go and get six metres of raw silk, a card of
 * matching buttons and two metres of piping. Until now nothing in the product held that. The
 * purchasing module knows what was ordered from a supplier and the inventory module knows what is
 * on the shelf, and neither of them can answer the question the person on the counter actually
 * asks: *this order needs this much of this — where has that got to?*
 *
 * SO A SOURCING REQUIREMENT IS A NEED, NOT A PURCHASE. It hangs off an order (and optionally off
 * one garment of that order), records a kind of material, how much, what it is expected to cost
 * and how far along getting it is. Turning a need into a purchase order against a supplier stays
 * purchasing's job. The `purchaseOrderId` / `purchaseOrderNumber` pair exists so that a raised PO
 * can later be pointed back at the need it satisfied; nothing in this module writes it, and it is
 * null on every row this module creates.
 *
 * THE NUMBERS ARE INTEGERS, both of them, and for the two reasons the rest of the codebase already
 * gives: money is an integer count of paise (`money/paise.ts`) and quantity is an integer count of
 * milli-units (`inventory/units.ts`). Six and a half metres is 6500, not 6.5, so that a card
 * totalling a dozen rows is exact however long the shop keeps it.
 */

/* -------------------------------------------------------------------------------------------
 * Kind and status
 * ---------------------------------------------------------------------------------------- */

/**
 * The two kinds of thing an order needs, kept coarse on purpose.
 *
 * `fabrics` is the cloth the garment is cut from; `trims` is everything fastened to it — buttons,
 * piping, lace, zips, hooks. A finer taxonomy already exists on the stock item (see
 * `STOCK_CATEGORIES`), and duplicating it here would mean two lists to keep in step for a
 * distinction nobody sourcing material actually makes: the fabric shop and the trims shop are
 * different shops, and that is the split this filter is for.
 */
export const SOURCING_TYPES = ['fabrics', 'trims'] as const;
export type SourcingType = (typeof SOURCING_TYPES)[number];
export const sourcingTypeSchema = z.enum(SOURCING_TYPES);

/**
 * How far along getting it is. Four values, and two of them are endings.
 *
 * A new row is always `to_start` — nobody has gone for it yet. `received` means the material is in
 * the shop; `cancelled` means the need went away (the customer changed the design, the garment was
 * dropped). Both are terminal, and a terminal row is a record rather than a working sheet: it can
 * be read but not edited, re-advanced or removed.
 */
export const SOURCING_STATUSES = ['to_start', 'in_progress', 'received', 'cancelled'] as const;
export type SourcingStatus = (typeof SOURCING_STATUSES)[number];
export const sourcingStatusSchema = z.enum(SOURCING_STATUSES);

/** Where a row starts. Nothing creates one in any other status. */
export const INITIAL_SOURCING_STATUS = 'to_start' satisfies SourcingStatus;

export const SOURCING_DESCRIPTION_MAX_LENGTH = 160;
export const SOURCING_NOTES_MAX_LENGTH = 500;
export const SOURCING_STATUS_NOTE_MAX_LENGTH = 300;

/**
 * The lifecycle, in one table.
 *
 * It lives in the shared package rather than in the service because BOTH SIDES NEED IT: the API
 * refuses anything not listed here with a 409, and the screen offers only what is listed here as a
 * next step. Two copies of this table is how a shop comes to be shown a button that the server
 * then refuses in front of a customer.
 *
 * Setting a row to the status it already holds is not a transition and is not listed — it is
 * almost always a double tap, and treating it as a no-op would quietly overwrite the note attached
 * to the first one.
 */
export const SOURCING_TRANSITIONS: Readonly<Record<SourcingStatus, readonly SourcingStatus[]>> =
  Object.freeze({
    to_start: Object.freeze(['in_progress', 'cancelled'] as const),
    in_progress: Object.freeze(['received', 'cancelled'] as const),
    received: Object.freeze([] as const),
    cancelled: Object.freeze([] as const),
  });

/** Every status a row in `from` may legally be moved to. Empty once it is finished. */
export function nextSourcingStatuses(from: SourcingStatus): readonly SourcingStatus[] {
  return SOURCING_TRANSITIONS[from];
}

export function canTransitionSourcing(from: SourcingStatus, to: SourcingStatus): boolean {
  return SOURCING_TRANSITIONS[from].includes(to);
}

/** True once the row is a record rather than a working sheet — no edit, no delete, no advance. */
export function isSourcingFinished(status: SourcingStatus): boolean {
  return status === 'received' || status === 'cancelled';
}

/* -------------------------------------------------------------------------------------------
 * The row
 * ---------------------------------------------------------------------------------------- */

export const sourcingRequirementSchema = z.object({
  id: objectIdSchema,

  /** The order the need belongs to. Denormalised so the list renders without a join per row. */
  orderId: objectIdSchema,
  orderNumber: z.string(),
  /** One garment of that order, or null when the need is the order's as a whole. */
  orderItemId: objectIdSchema.nullable(),
  /**
   * What the row is FOR, in the words the shop reads: the garment type label when a garment is
   * linked, and the free-text description when the row hangs off the order itself.
   */
  productLabel: z.string(),

  customerId: objectIdSchema,
  customerName: z.string(),

  type: sourcingTypeSchema,
  description: z.string().nullable(),

  /** Integer milli-units. See the note at the top of this file — never a float. */
  quantity: milliUnitsSchema,
  unit: stockUnitSchema,
  /** What it is expected to cost, in integer paise. An estimate, not a bill. */
  amountPaise: nonNegativePaiseSchema,

  status: sourcingStatusSchema,
  notes: z.string().nullable(),

  /**
   * The purchase order that eventually covered this need, once purchasing raises one.
   *
   * ALWAYS NULL on anything this module writes. It is here so that the link, when a later task
   * builds it, has somewhere to land — not because sourcing raises purchase orders. It does not.
   */
  purchaseOrderId: objectIdSchema.nullable(),
  purchaseOrderNumber: z.string().nullable(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type SourcingRequirement = z.infer<typeof sourcingRequirementSchema>;

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

export const createSourcingRequirementSchema = z.object({
  orderId: objectIdSchema,
  /** Optional: a need may belong to one garment, or to the booking as a whole. */
  orderItemId: objectIdSchema.optional(),
  type: sourcingTypeSchema,
  description: z.string().trim().max(SOURCING_DESCRIPTION_MAX_LENGTH).optional(),
  quantity: positiveMilliUnitsSchema,
  unit: stockUnitSchema,
  amountPaise: nonNegativePaiseSchema,
  notes: z.string().trim().max(SOURCING_NOTES_MAX_LENGTH).optional(),
});
export type CreateSourcingRequirementInput = z.input<typeof createSourcingRequirementSchema>;

/**
 * What may be corrected afterwards.
 *
 * `orderId` and `orderItemId` are FIXED once the row exists. Moving a need from one order to
 * another is not a correction, it is two different needs, and allowing it would silently change
 * what an order's card totals to.
 */
export const updateSourcingRequirementSchema = createSourcingRequirementSchema
  .omit({ orderId: true, orderItemId: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateSourcingRequirementInput = z.input<typeof updateSourcingRequirementSchema>;

/**
 * Moving a row along. `note` is what somebody wants recorded about the move — "Kumaran had no
 * matching thread, trying Ranganathan Street" — and it is written onto `notes` only when supplied,
 * so advancing a row without saying anything leaves whatever note was already there alone.
 */
export const setSourcingStatusSchema = z.object({
  status: sourcingStatusSchema,
  note: z.string().trim().max(SOURCING_STATUS_NOTE_MAX_LENGTH).optional(),
});
export type SetSourcingStatusInput = z.input<typeof setSourcingStatusSchema>;

/**
 * The standalone screen's query. `status` repeats for several, because the view the screen opens
 * on is the unfinished work — `to_start` and `in_progress` together — rather than any one status.
 */
export const sourcingSearchQuerySchema = paginationQuerySchema.extend({
  /** Free text: a customer's name, an order number, or what the row is for. */
  q: z.string().trim().max(120).optional(),
  status: z
    .union([sourcingStatusSchema, z.array(sourcingStatusSchema)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional(),
  type: sourcingTypeSchema.optional(),
  orderId: objectIdSchema.optional(),
});
export type SourcingSearchQuery = z.infer<typeof sourcingSearchQuerySchema>;

/* -------------------------------------------------------------------------------------------
 * Responses
 * ---------------------------------------------------------------------------------------- */

export const sourcingRequirementPageSchema = z.object({
  items: z.array(sourcingRequirementSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SourcingRequirementPage = z.infer<typeof sourcingRequirementPageSchema>;

/**
 * One order's needs, in the order they were written down. What the card on the order reads.
 *
 * `totalPaise` sums the NON-CANCELLED rows only. A cancelled need costs nothing — counting it
 * would tell an owner that a lehenga needs three thousand rupees of material when one of the three
 * was dropped a week ago.
 */
export const orderSourcingSchema = z.object({
  orderId: objectIdSchema,
  orderNumber: z.string(),
  items: z.array(sourcingRequirementSchema),
  totalPaise: nonNegativePaiseSchema,
});
export type OrderSourcing = z.infer<typeof orderSourcingSchema>;
